/**
 * LetterDetail.tsx — silnik formatek (R3, unifikacja-formatek — mirror
 * CommercialDocDetail/PurchaseInvoiceDetail).
 *
 * Jeden komponent dla 3 ścieżek:
 *   - /letters/new → tryb create (form pusty, edit mode od razu)
 *   - /letters/:id → tryb view (read-only, przycisk Edytuj)
 *   - /letters/:id + Edytuj → tryb edit (PATCH, przyciski Zapisz/Anuluj)
 *
 * Layout (silnik formatek — descent, JEDNA ścieżka create/edit/view):
 *   JEDEN <RecursiveFormRenderer> na layoucie po `topUpDocumentLayout`
 *   (syntetyzuje 'data' z pól core gdy brak aktywnego layoutu → gałąź
 *   `!effectiveLayoutConfig` usunięta). `renderField` z
 *   `useKartotekaRenderField(letterEngineConfig, …)` — core przez nadpisania
 *   `letterCoreRenderers.tsx` (LETTER_STATUSES, source read-only na sztywno,
 *   externalId/externalSource disable cross-field), ext przez ExtFieldValueInput;
 *   pole dodatkowe pokazuje się tylko tam, gdzie ktoś postawił je w układzie jako
 *   zwykłe pole (top-up niczego nie dokleja).
 *   dokument-blokify (2026-08-24): 'audit_log' NIE jest już zaszytą zakładką (top-up
 *   jej nie wymusza) — to przenośny blok palety (letter_audit_view) dispatchowany
 *   przez contextRenderers, usuwalny/dodawalny jak inne bloki. Samodzielnej zakładki
 *   „Pola dodatkowe" (letter_ext_fields_view) już nie ma — pole dodatkowe edytuje się
 *   tylko tam, gdzie zostało postawione w układzie, a jego wartość przychodzi z odpowiedzi
 *   GET rekordu.
 *   tabRenderers mapuje pozostałe zakładki (NIE 'data' — ta schodzi rekurencyjnie):
 *     parties     → DocumentPartiesEditor (guard: !isCreate && !!id, SEPARATE
 *                   self-persisting sub-resource, orthogonal do atomic save)
 *   contextRenderers → letter_audit_view (przenośny blok, guard !isCreate &&
 *     recordId) + buildDocumentAdditionalRenderers(recordId, { buffered: true, ... })
 *     — tabele dodatkowe (additional_view/satellite_view) dostępne JUŻ w create,
 *     bez HTTP, delty jadą w POST/PATCH.
 *
 * Save flow (atomic, mirror Krok 4 — saveAggregate) — NIEZMIENIONE migracją silnika:
 *   - create: POST /letters z pełnym body (pola pisma + extValues +
 *     additionalTables, gdy niepuste) → jeden zapis w jednej transakcji.
 *   - edit:   PATCH /letters/:id analogicznie — jedno wywołanie, bez
 *     osobnego PUT na ext-fields (usunięty; ext-fields i delty tabel
 *     dodatkowych jadą inline w body POST/PATCH, patrz saveAggregate).
 *
 * Uprawnienie manage_parties: documents.letter.manage_parties
 * System-module slug: letters
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UUID_REGEX } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mail, Pencil, Plus, RotateCcw, Save, Trash2, X as XIcon } from 'lucide-react';

import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentItemsEditor } from '@/components/documents/DocumentItemsEditor';
import { DocumentItemsSection } from '@/components/documents/DocumentItemsSection';
import { DocumentLayoutPicker } from '@/components/documents/DocumentLayoutPicker';
import { DocumentPartiesEditor } from '@/components/documents/DocumentPartiesEditor';
import { DocumentTypeChangeBadge } from '@/components/documents/DocumentTypeChangeBadge';
import { DocumentTypeCreateBadge } from '@/components/documents/DocumentTypeCreateBadge';
import {
  type TopUpContext,
  topUpDocumentLayout,
} from '@/components/documents/engine/topUpDocumentLayout';
import { letterEngineConfig } from '@/components/documents/letterEngineConfig';
import { EMPTY_LETTER_FORM, type LetterFormData } from '@/components/documents/letterFormTypes';
import { RecursiveFormRenderer } from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import {
  buildCoreFieldRecordValues,
  isSlugMatch,
} from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { ModuleGuard } from '@/components/ModuleGuard';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { invalidateRecordInstances } from '@/hooks/useActiveInstanceForRecord';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useControlWarning } from '@/hooks/useControlWarning';
import { useDocumentDetailTab } from '@/hooks/useDocumentDetailTab';
import { useDocumentSqlButton } from '@/hooks/useDocumentSqlButton';
import { useDocumentTypeAndLayoutParams } from '@/hooks/useDocumentTypeAndLayoutParams';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import { applyCoreDefaults, useNewRecordDefaults } from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import {
  type DocumentHeaderAmountsFields,
  type DocumentLineItemDraft,
  type DocumentLineItemsFields,
} from '@/lib/documents/document-items';
import {
  draftToPayload,
  serverItemToDraft,
  validateItemsDraft,
} from '@/lib/documents/document-items-draft';
import { letterStatusLabel } from '@/lib/documents/document-select-labels';
import { useNumberingBaseFieldSlug } from '@/lib/documents/useNumberingBaseFieldSlug';
import { extractErrorCode } from '@/lib/errors';
import { type ProcessWarning, processWarningMessage } from '@/lib/processes/approvals-api';
import { DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { useMyModules } from '@/lib/use-my-modules';
import {
  buildExtFieldServerMessages,
  buildFieldIssueMessages,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';

import { buildDocumentAdditionalRenderers } from './document-additional-renderers';

// ─── Types ──────────────────────────────────────────────────────────────────

interface LetterDetail
  extends LetterFormData, DocumentLineItemsFields, DocumentHeaderAmountsFields {
  id: string;
  companyId: string;
  // systemNumber jest dziedziczony z LetterFormData (read-only, patrz letterFormTypes.ts).
  gcrecord: string | null;
  createdAt: string;
  updatedAt: string;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function LetterDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();

  // ── Permissions ──
  // /api/auth/me zwraca dziś puste `permissions` (wypełnienie odroczone) — pusta lista
  // oznacza „brak wiedzy", nie „brak uprawnień": pokazujemy akcje, backend egzekwuje.
  const permissionsUnknown = !!me && me.permissions.length === 0;

  const canManageParties =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.letter.manage_parties') === true;

  const canEdit =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.letter.edit') === true;

  const canDelete =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.letter.delete') === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, setTab } = useDocumentDetailTab();

  // ── Local state ──
  const [editing, setEditing] = useState(isCreate);
  // Rework D.8 — typ wybrany w edycji, niezapisany do czasu głównego zapisu; reset
  // przy zmianie rekordu (mirror layoutOverrideId w useDocumentTypeAndLayoutParams).
  const [pendingDocumentTypeId, setPendingDocumentTypeId] = useState<string | null>(null);
  useEffect(() => {
    setPendingDocumentTypeId(null);
  }, [id]);
  const [form, setForm] = useState<LetterFormData>(() => ({
    ...EMPTY_LETTER_FORM,
    documentTypeId: isCreate ? (searchParams.get('documentTypeId') ?? null) : null,
  }));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);

  // Draft edytowalnych pozycji — inicjalizowany z odpowiedzi serwera (efekt niżej),
  // wysyłany jako tablica `items` w tym samym zapisie co dokument. `itemsDirty`
  // steruje semantyką API: wysyłamy `items` TYLKO gdy użytkownik ruszył grid (inaczej
  // pomijamy → backend nie tyka pozycji; bez zbędnego replace-setu przy edycji nagłówka).
  const [itemsDraft, setItemsDraft] = useState<DocumentLineItemDraft[]>([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  // Klucze wierszy niekompletnych z ostatniej próby zapisu (patrz handleSave) —
  // podświetlenie w edytorze, zerowane przy kolejnej udanej walidacji.
  const [invalidItemRowKeys, setInvalidItemRowKeys] = useState<Set<string>>(new Set());
  const handleItemsChange = (next: DocumentLineItemDraft[]) => {
    setItemsDraft(next);
    setItemsDirty(true);
  };

  // Atomic-save refactor (mirror ContractDetail/SaleInvoiceDetail): bufor delt
  // tabel dodatkowych — jeden wpis per tableId, agregowany z każdej instancji
  // <AdditionalTableTab buffered> na tej stronie. Wyciągnięte do współdzielonego
  // hooka (Etap 2.2, unifikacja-formatek) — pełny kontrakt i uzasadnienie
  // freshness `getBufferedValue` w nagłówku `useAdditionalTableDeltas.ts`.
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  // Podswietlenie pol odrzuconych przez walidacje serwera (VALIDATION_ERROR) —
  // czerwona ramka bez tekstu, patrz onError w saveMutation nizej + hasError
  // przekazany do useKartotekaRenderField.
  const fe = useFieldErrors();

  // Walidacja klienta przed wysyłką (mirror PurchaseInvoiceDetail — parytet
  // client-side blokady niekompletnych wierszy pozycji): grid pozycji ruszony —
  // zablokuj zapis, gdy któryś wiersz nie ma kompletu pól wymaganych przez
  // backend (patrz `validateItemsDraft`), zamiast wysyłać niekompletny wiersz i
  // pokazywać dopiero odpowiedź 400 serwera.
  // Zwraca, czy zapis wystartował (`false` = zatrzymała go walidacja) — z tego korzysta
  // `useSaveBeforeDecision` (klik decyzji w banerze w edycji = najpierw ten sam zapis).
  function handleSave(): boolean {
    if (itemsDirty && itemsDraft.length > 0) {
      const invalidRows = validateItemsDraft(itemsDraft);
      if (invalidRows.size > 0) {
        setInvalidItemRowKeys(invalidRows);
        toast.show({
          variant: 'error',
          title: t('tenant.documents.items.editor.rowsIncomplete'),
        });
        return false;
      }
    }
    setInvalidItemRowKeys(new Set());
    saveMutation.mutate(form);
    return true;
  }

  // ── Fetch detail ──
  const detailQ = useQuery({
    queryKey: ['letter', id],
    queryFn: async () => {
      const res = await api.get<{ letter: LetterDetail }>(`/letters/${id}`);
      return res.data.letter;
    },
    enabled: !isCreate && !!id,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowego pisma — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('letters', isCreate);

  // Ext-fields (pola dodatkowe): lokalny stan zasilany wartością z odpowiedzi GET rekordu
  // (`detailQ.data.extValues`, bez osobnego żądania) — pełny kontrakt synchronizacji
  // w nagłówku `useRecordExtValues.ts`. Przy tworzeniu „wartością serwera” są wartości
  // domyślne pól dodatkowych.
  const { extValues, handleExtChange, markSaved, snapshotEdits, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // T1.6 (layouty-e1): typ dokumentu do rozstrzygnięcia layoutu — duplikat
  // (walidacja UUID) walidacji `effectiveDocumentTypeId` z
  // `useDocumentTypeAndLayoutParams` niżej — niemożliwe do uniknięcia:
  // ten hook aktywnego layoutu MUSI zostać na stronie i potrzebuje typu PRZED
  // wywołaniem `useDocumentTypeAndLayoutParams` (który z kolei przyjmuje
  // `layoutConfig` STĄD jako `activeLayoutConfig`). Świadomie NIE gate'ujemy
  // hooka poniżej `enabled: false` do czasu załadowania detalu — jeden
  // dodatkowy fetch bez typu (podczas ładowania detalu) + refetch z typem po
  // jego przyjściu, zero ryzyka przedwczesnego renderu fallbacku.
  const rawCreateDocumentTypeIdForLayout = isCreate ? searchParams.get('documentTypeId') : null;
  const documentTypeIdForLayout = isCreate
    ? rawCreateDocumentTypeIdForLayout && UUID_REGEX.test(rawCreateDocumentTypeIdForLayout)
      ? rawCreateDocumentTypeIdForLayout
      : null
    : (pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null);

  // dynamic-layout: layout z F12.N buildera + filtrowanie zakładek per rola
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } = useSystemModuleActiveLayout(
    'letters',
    {
      documentTypeId: documentTypeIdForLayout,
    },
  );

  // Etap 2.3 (unifikacja-formatek): create-params / layout-override cluster
  // wyciągnięty do współdzielonego hooka — pełny kontrakt i uzasadnienie
  // (redirect guard, walidacja UUID, reset override przy zmianie rekordu) w
  // nagłówku `useDocumentTypeAndLayoutParams.ts`.
  const {
    createCompanyId,
    missingDocumentTypeId,
    effectiveDocumentTypeId,
    layoutOverrideId,
    setLayoutOverrideId,
    effectiveLayoutConfig,
  } = useDocumentTypeAndLayoutParams({
    isCreate,
    searchParams,
    navigate,
    listRoute: withMenuItem('/letters'),
    moduleSlug: 'letters',
    recordId: id ?? null,
    detailDocumentTypeId: documentTypeIdForLayout,
    activeLayoutConfig: layoutConfig,
  });

  // Silnik formatek (R3, unifikacja-formatek): renderField dla core+ext przez
  // config pisma (nadpisania widgetów = letterCoreRenderers.tsx, delegacja ext
  // do ExtFieldValueInput). `hasError` (patrz fe/useFieldErrors wyżej) podświetla
  // czerwoną ramką pola odrzucone przez VALIDATION_ERROR — natywna delegacja
  // (title/documentDate/description/notes/rejectionReason) łapie to automatycznie
  // w native branch silnika, nadpisania (status/urgency/externalId/externalSource)
  // przez letterCoreRenderers.tsx.
  // Gwiazdka „wymagane" przy polu daty numeracji wybranego typu (patrz PurchaseInvoiceDetail).
  const numberingBaseFieldSlug = useNumberingBaseFieldSlug(
    'letter',
    effectiveDocumentTypeId,
    createCompanyId ?? detailQ.data?.companyId ?? null,
  );

  const { renderField, fields: kartotekaFields } = useKartotekaRenderField(letterEngineConfig, {
    form,
    onChange: setForm,
    extValues,
    onExtChange: handleExtChange,
    editable: editing,
    isCreate,
    isFieldExtraRequired: isSlugMatch(numberingBaseFieldSlug),
    companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
    hasError: (key) => !!fe.get(String(key)),
    getError: (key) => fe.get(String(key)),
    clearError: (key) => fe.clear(String(key)),
  });

  // Top-up (bezwarunkowy, idempotentny) na layoucie EFEKTYWNYM — syntetyzuje
  // zakładkę 'data' z pól core gdy brak aktywnego layoutu i dosypuje
  // 'parties' (dedup po slugu). 'parties' BEZ
  // placeholderType — treść niesie tabRenderers['parties'] (self-persisting
  // sub-resource), top-up tylko gwarantuje istnienie zakładki i jej pozycję.
  const topUpCtx = useMemo<TopUpContext>(
    () => ({
      // dokument-blokify (2026-08-24): 'ext_fields'/'audit_log' USUNIĘTE z pageTabs —
      // top-up dosypywał je bezwarunkowo przy każdym renderze (nawet po świadomym usunięciu
      // w builderze), co łamało portability. Teraz blok audytu jest zasiany jako NORMALNE
      // dziecko w domyślnym (seedowanym) layoucie `letters` (mirror kartotek); pól dodatkowych
      // top-up ani seed nie dokleja — są tylko tam, gdzie stoją w układzie. Zostają 'data'/'parties'.
      pageTabs: [
        { slug: 'data', title: t('tenant.documents.shared.tabs.data'), sortOrder: 0 },
        { slug: 'parties', title: t('tenant.documents.shared.tabs.parties'), sortOrder: 1 },
      ],
      coreFieldSlugs: letterEngineConfig.coreFieldSlugs,
    }),
    [t],
  );
  const engineLayout = useMemo(
    () => topUpDocumentLayout(effectiveLayoutConfig, topUpCtx),
    [effectiveLayoutConfig, topUpCtx],
  );

  // ── Sync server → form ── W trybie create formularz startuje z wartości domyślnych pól
  // (nadpisują wartości startowe z kodu); documentTypeId z URL nakładamy PO nich.
  useEffect(() => {
    if (isCreate) {
      setForm({
        ...applyCoreDefaults(EMPTY_LETTER_FORM, recordDefaults.core),
        documentTypeId: searchParams.get('documentTypeId') ?? null,
      });
      return;
    }
    // Rework D.8 — nie nadpisuj niezapisanych zmian w trakcie edycji: odświeżenie
    // detalu w tle (inna mutacja/zakładka unieważniająca ten klucz) nie może cofnąć
    // edytowanego formularza. Po zapisie efekt dogania świeży stan serwera, bo
    // `editing` wraca na false w tym samym onSuccess co invalidate.
    if (editing) return;
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        title: d.title,
        status: d.status,
        urgency: d.urgency,
        description: d.description,
        notes: d.notes,
        documentDate: d.documentDate,
        source: d.source,
        externalId: d.externalId,
        externalSource: d.externalSource,
        rejectionReason: d.rejectionReason,
        ownerUserId: d.ownerUserId ?? null,
        categoryId: d.categoryId ?? null,
        systemNumber: d.systemNumber,
      });
    }
    // documentTypeId czytamy wyłącznie przy pierwszym mount'cie / SPA-nawigacji na /new;
    // dołączenie searchParams powodowałoby reset formularza przy każdej zmianie taba.
    // `recordDefaults.core` zmienia referencję raz (wczytanie) — pola są wtedy jeszcze ukryte.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams świadomie pominięty (patrz wyżej)
  }, [detailQ.data, isCreate, editing, recordDefaults.core]);

  // Sync server → draft pozycji (przy załadowaniu detalu i po zapisie). Gdy użytkownik
  // ma lokalne zmiany (`itemsDirty`) — draft zostaje nietknięty. Po zapisie onSuccess
  // zeruje `itemsDirty`, więc ten efekt resynchronizuje ze świeżym stanem serwera.
  useEffect(() => {
    if (itemsDirty) return;
    setItemsDraft((detailQ.data?.items ?? []).map(serverItemToDraft));
  }, [detailQ.data?.items, itemsDirty]);

  // Klik decyzji w banerze procesu, gdy formularz jest w edycji: najpierw ten sam zapis co
  // „Zapisz", okno decyzji dopiero po jego sukcesie (useSaveBeforeDecision; „zapis już trwa"
  // sprawdza sam hak po kluczu `saveMutationKey` mutacji zapisu tej kopii formularza).
  // `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError` i z anulowania
  // ostrzeżenia kontroli niżej.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: id,
    save: handleSave,
  });

  // Przycisk SQL (klocek `sql_button`): zadania w tle na tym piśmie — stan, pasek blokady, renderer
  // klocka, utworzenie → zadanie → przejście (patrz nagłówek `useDocumentSqlButton`).
  const sqlButton = useDocumentSqlButton({
    recordClass: 'letter',
    isCreate,
    recordId: id,
    layoutConfig: effectiveLayoutConfig,
    save: handleSave,
    createCompanyId,
    saveBeforeDecision,
  });

  // Kontrola dokumentu (F20+ reużywalny hook, patrz useControlWarning) — `mutate`
  // celowo owinięty w lambdę: `saveMutation` deklarowana niżej, ale to wywołanie
  // trafia dopiero do zamknięcia `handleConfirm`, uruchamianego po pełnym mount'cie.
  const controlWarning = useControlWarning<LetterFormData & { warningsAccepted?: boolean }>({
    mutate: (payload) => saveMutation.mutate(payload),
    // Anulowane ostrzeżenie kontroli = zapis się nie odbył → okno decyzji się nie otwiera, a zadanie
    // przycisku SQL kliknięte na nowym piśmie nie rusza.
    onCancel: sqlButton.onSaveAborted,
  });

  // ── Save mutation (POST create / PATCH edit) ──
  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: LetterFormData & { warningsAccepted?: boolean }) => {
      const cleaned: Record<string, unknown> = {
        ...(data.title ? { title: data.title } : {}),
        status: data.status,
        urgency: data.urgency,
        description: data.description ?? null,
        notes: data.notes ?? null,
        documentDate: data.documentDate ?? null,
        ownerUserId: data.ownerUserId ?? null,
        categoryId: data.categoryId ?? null,
        externalId: data.externalId ?? null,
        externalSource: data.externalSource ?? null,
        rejectionReason: data.rejectionReason ?? null,
      };

      // Rework D.8 — typ dokumentu wchodzi do payloadu przy tworzeniu ZAWSZE, a przy
      // edycji TYLKO gdy user faktycznie wybrał inny typ (pendingDocumentTypeId). Bez
      // warunku edycja wysyłałaby dziś `null` (schema PATCH: documentTypeId optional,
      // NIE nullable) i dostawała 400.
      if (isCreate) {
        cleaned.documentTypeId = data.documentTypeId || null;
      } else if (
        pendingDocumentTypeId !== null &&
        pendingDocumentTypeId !== detailQ.data?.documentTypeId
      ) {
        cleaned.documentTypeId = pendingDocumentTypeId;
      }

      // Kontrola dokumentu (tenant hook `document_control`) — ostrzeżenie miękkie
      // ponawiane z tą flagą po potwierdzeniu w dialogu (patrz useControlWarning).
      if (data.warningsAccepted) {
        cleaned.warningsAccepted = true;
      }

      // Atomic-save refactor (mirror ContractDetail): jeden zapis — extValues +
      // delty tabel dodatkowych jadą w TYM SAMYM body co pola pisma (backend
      // zapisuje wszystko w jednej transakcji, patrz saveAggregate). Pola
      // opcjonalne — pomijamy gdy puste, zachowanie identyczne jak dotąd dla
      // callerów bez nich.
      // Migawka edycji tuż przed włożeniem `extValues` do żądania — PRZED oczekiwaniem na
      // definicje pól tabel (`buildAdditionalParentValues`): edycja zrobiona w tym czasie nie
      // trafia do body, więc `markSaved()` w onSuccess jej nie zdejmie (nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        cleaned.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        cleaned.additionalTables = additionalTablesBody;
        // Wartości pól pisma z ekranu dla wiązań `parent_field` w wierszach tabel dodatkowych
        // — OBOK delt, liczone ze stanu przekazanego do zapisu (`data`), tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(
            extValues,
            buildCoreFieldRecordValues(letterEngineConfig, data),
          ),
          'letter',
        );
        if (additionalParentValues) {
          cleaned.additionalParentValues = additionalParentValues;
        }
      }

      // Increment 2: pozycje jadą w TYM SAMYM body (replace-set; kwoty pochodne i
      // amount_* przelicza silnik serwera w tej samej transakcji). Wysyłamy TYLKO gdy
      // grid ruszony — inaczej pomijamy `items` (backend nie tyka pozycji). Pusty draft
      // po ruszeniu = `[]` = kasacja pozycji + powrót do trybu kwot ręcznych.
      if (itemsDirty) {
        cleaned.items = itemsDraft.map(draftToPayload);
      }

      let recordId: string;
      let processWarning: ProcessWarning | undefined;
      if (isCreate) {
        // CORE INVARIANT: w trybie „Wszystkie firmy" interceptor NIE wysyła
        // X-Company-Id (brak aktywnej firmy) — bez jawnego nagłówka backend
        // (`requireCompany`) odrzuciłby POST z MISSING_HEADER, nawet gdy
        // documentTypeId jednoznacznie wskazuje firmę. Jawny nagłówek per-request
        // wygrywa z interceptorem (patrz `lib/api.ts` — nie nadpisuje już ustawionego
        // X-Company-Id) i działa identycznie w trybie jednej aktywnej firmy.
        const res = await api.post<{
          id: string;
          systemNumber: string | null;
          processWarning?: ProcessWarning;
        }>(
          '/letters',
          cleaned,
          createCompanyId ? { headers: { 'X-Company-Id': createCompanyId } } : undefined,
        );
        recordId = res.data.id;
        processWarning = res.data.processWarning;
      } else {
        await api.patch(`/letters/${id}`, cleaned);
        recordId = id!;
      }

      return { id: recordId, processWarning };
    },
    onSuccess: async (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      void queryClient.invalidateQueries({ queryKey: ['letters'] });
      // Zapis mógł zmienić typ dokumentu (a od niego zależą prawa obiegu): flagi „Wyślij do
      // akceptacji" / „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie).
      void invalidateRecordInstances(queryClient, DOCUMENTS_MODULE_CODE, data.id);
      // Awaitowane: bez tego setItemsDirty(false)/setEditing(false) (niżej) flipowały
      // stan PRZED odświeżeniem detalu, co na chwilę pokazywało stare pozycje przed
      // resyncem draftu (post-save stale-items flash). Reszta invalidacji zostaje
      // fire-and-forget — tylko ta jedna gatekeeperuje UI po zapisie pozycji.
      // Pola dodatkowe zapisane razem z dokumentem — od tej chwili wartość serwera (z odświeżonego
      // detalu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      await queryClient.invalidateQueries({ queryKey: ['letter', data.id] });
      // Atomic-save refactor (mirror ContractDetail): po zapisie odśwież
      // rekordy tabel dodatkowych (prefix match — łapie wszystkie
      // ['additional-records', tableId, parentId]) i wyczyść lokalny bufor
      // delt — backend już je zapisał atomowo.
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row też czyścimy
      // po udanym zapisie — spójnie z buforem zatwierdzonych wierszy powyżej.
      resetAddRowDrafts();
      // Pozycje zapisane atomowo — zeruj dirty, żeby efekt sync odświeżył draft ze
      // świeżego stanu serwera po refetchu detalu.
      setItemsDirty(false);

      if (isCreate) {
        toast.show({
          variant: 'success',
          title: t('tenant.documents.letters.detail.toast.created'),
        });
        if (data.processWarning) {
          toast.show({
            variant: 'warning',
            title: processWarningMessage(t, data.processWarning),
          });
        }
        // Przycisk SQL kliknięty na nowym piśmie: zadanie rusza TERAZ — z id nowego pisma, PRZED
        // przejściem pod jego adres. Porażka zlecenia dostaje własny komunikat; pismo już istnieje,
        // więc przejście i tak następuje.
        await sqlButton.runPendingCreateRun(data.id);
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
        // reset do podglądu, bo create→detail nie remontuje strony.
        setEditing(false);
        navigate(withMenuItem(`/letters/${data.id}`), { state: { replaceTab: true } });
        return;
      }
      toast.show({
        variant: 'success',
        title: t('tenant.documents.letters.detail.toast.updated'),
      });
      setPendingDocumentTypeId(null);
      setEditing(false);
      // Zapis wywołany kliknięciem decyzji w banerze → teraz otwórz okno decyzji (no-op, gdy
      // zapis poszedł zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu nie ma banera.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      // Zapis wywołany kliknięciem decyzji w banerze: każdy błąd poza ostrzeżeniem kontroli
      // kończy sprawę (okno decyzji się nie otworzy). O `CONTROL_WARNING` rozstrzyga user w
      // dialogu: potwierdzenie ponawia zapis (okno otworzy jego sukces), anulowanie woła
      // `onCancel`. Na samym początku, żeby nic w dalszej obsłudze błędu nie mogło tego pominąć.
      // To samo dla zadania przycisku SQL klikniętego na nowym piśmie: utworzenie się nie udało → nie rusza.
      if (code !== 'CONTROL_WARNING') sqlButton.onSaveAborted();
      // Podświetlenie + tekst pod polem odrzuconym przez serwer musi zobaczyć KAŻDĄ
      // odpowiedź, zanim jakakolwiek gałąź zrobi wczesny `return` (patrz JSDoc
      // `useFieldErrors.ts`). Kolejność: (1) polski z issue Zoda, (2) własny komunikat
      // serwera z `details` (reguły krzyżowe), (3) generyk. Angielskie default-y Zoda
      // nie przeciekają (pola z issue mają wpis w fieldMsgs i wygrywają przed messages[0]).
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      // Kontrola dokumentu (CONTROL_ERROR/CONTROL_WARNING/CONTROL_HOOK_FAILED) ma
      // pierwszeństwo — jeśli obsłużona (toast lub dialog już pokazany), reszta gałęzi
      // (specyficzne dla pism) nie musi się odpalać.
      if (controlWarning.handleError(err, form)) return;
      // Zapis odrzucony, bo na piśmie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „nie udało się zapisać”; odświeżony stan zadań pokaże pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      if (code === 'VALIDATION_ERROR') {
        const specific = buildValidationToastMessage(
          err,
          (field) => resolveFieldLabel(kartotekaFields, field),
          t,
        );
        toast.show({
          variant: 'error',
          title: specific ?? t('tenant.documents.letters.detail.toast.validationError'),
          // Brak wymaganego pola dodatkowego serwer zgłasza w `details` (bez `issues`), więc
          // `specific` jest wtedy puste — nazwij brakujące pola komunikatem serwera.
          description: specific ? undefined : buildExtFieldServerMessages(err, kartotekaFields),
        });
        return;
      }
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna zamiast ogólnego „błąd zapisu".
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.documents.letters.detail.toast.genericError'),
      });
    },
  });

  // Kontrola dokumentu (F20+ reużywalny hook) dla flow DELETE — osobne klucze i18n
  // ("Usuń mimo to") niż flow SAVE, patrz useControlWarning.
  const deleteControlWarning = useControlWarning<{ warningsAccepted?: boolean }>({
    mutate: (payload) => deleteMutation.mutate(payload),
    titleKey: 'tenant.documents.controlWarning.deleteTitle',
    confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
  });

  // ── Delete mutation ──
  const deleteMutation = useMutation({
    mutationFn: async (variables: { warningsAccepted?: boolean } = {}) => {
      const query = variables.warningsAccepted ? '?warningsAccepted=true' : '';
      await api.delete(`/letters/${id}${query}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.letters.detail.toast.deleted'),
      });
      void queryClient.invalidateQueries({ queryKey: ['letters'] });
      navigate(withMenuItem('/letters'));
    },
    onError: (err, variables) => {
      if (deleteControlWarning.handleError(err, variables ?? {})) return;
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.documents.letters.detail.toast.deleteError'),
      });
    },
  });

  // ── Restore mutation ──
  const restoreMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/letters/${id}/restore`, {});
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.letters.detail.toast.restored'),
      });
      void queryClient.invalidateQueries({ queryKey: ['letters'] });
      void queryClient.invalidateQueries({ queryKey: ['letter', id] });
    },
    onError: (err) => {
      // Przywrócenie odrzucone na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.documents.letters.detail.toast.restoreError'),
      });
    },
  });

  // ── Cancel / reset form ──
  function handleCancel() {
    fe.clearAll();
    setPendingDocumentTypeId(null);
    if (isCreate) {
      navigate(withMenuItem('/letters'));
      return;
    }
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        title: d.title,
        status: d.status,
        urgency: d.urgency,
        description: d.description,
        notes: d.notes,
        documentDate: d.documentDate,
        source: d.source,
        externalId: d.externalId,
        externalSource: d.externalSource,
        rejectionReason: d.rejectionReason,
        ownerUserId: d.ownerUserId ?? null,
        categoryId: d.categoryId ?? null,
        systemNumber: d.systemNumber,
      });
    }
    discardEdits();
    // Anuluj porzuca też zbuforowane zmiany tabel dodatkowych i szkice add-row (nie tylko zapis).
    resetAdditionalDeltas();
    resetAddRowDrafts();
    setEditing(false);
  }

  // ── Loading / error states ──
  // Redirect w toku (obsługiwany w useDocumentTypeAndLayoutParams) — nie renderuj formularza create
  // ani na moment, żeby user nie zobaczył migawki formularza bez typu dokumentu.
  if (missingDocumentTypeId) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="letter-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="letter-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div className="space-y-4 p-6">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
          onClick={() => navigate(withMenuItem('/letters'))}
        >
          {t('tenant.documents.letters.detail.backToList')}
        </Button>
        <div className="rounded-xl border border-status-error-border bg-status-error-bg p-6 text-sm text-status-error">
          {t('tenant.documents.letters.detail.notFound')}
        </div>
      </div>
    );
  }

  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="letter-detail" />;
  }

  const displayTitle = isCreate
    ? t('tenant.documents.letters.detail.createTitle')
    : (detailQ.data?.systemNumber ??
      detailQ.data?.title ??
      t('tenant.documents.letters.detail.unnamedTitle'));

  const isDeleted = !isCreate && !!detailQ.data?.gcrecord;

  const recordId = isCreate ? null : (id ?? null);
  const dockCompanyId = createCompanyId ?? detailQ.data?.companyId ?? undefined;

  // ── Render ──
  return (
    <ModuleGuard moduleCode="crm_core">
      <DocumentPreviewDockHost
        enabled={previewDockEnabled && !!recordId}
        moduleId="letters"
        ownerType="document"
        ownerId={recordId}
        companyId={dockCompanyId}
      >
        <div className="space-y-4 p-6" data-testid="letter-detail-page">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <IconButton
                onClick={() => navigate(withMenuItem('/letters'))}
                data-testid="letter-detail-back"
                aria-label={t('common.back')}
              >
                <ArrowLeft />
              </IconButton>
              <div className="flex items-start gap-3">
                <Mail className="mt-1 h-6 w-6 text-text-secondary" aria-hidden="true" />
                <div>
                  {isCreate ? (
                    <h1
                      className="text-2xl font-semibold text-text-primary"
                      data-testid="letter-detail-create-title"
                    >
                      {displayTitle}
                      <DocumentTypeCreateBadge
                        docClass="letter"
                        documentTypeId={effectiveDocumentTypeId}
                        companyId={createCompanyId}
                        testId="letter-detail-create-type-badge"
                        onChange={(newTypeId, newCompanyId) => {
                          const params = new URLSearchParams(searchParams);
                          params.set('documentTypeId', newTypeId);
                          params.set('companyId', newCompanyId);
                          setSearchParams(params, { replace: true });
                          setForm((prev) => ({ ...prev, documentTypeId: newTypeId }));
                        }}
                      />
                    </h1>
                  ) : (
                    <>
                      <h1
                        className="text-2xl font-semibold text-text-primary"
                        data-testid="letter-detail-title"
                      >
                        {displayTitle}
                        {canEdit && (
                          <DocumentTypeChangeBadge
                            docClass="letter"
                            documentTypeId={
                              pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null
                            }
                            companyId={detailQ.data?.companyId ?? null}
                            testId="letter-detail-change-type-badge"
                            disabled={!editing}
                            onConfirm={(newTypeId) => {
                              setPendingDocumentTypeId(newTypeId);
                              setLayoutOverrideId(null);
                            }}
                          />
                        )}
                        {pendingDocumentTypeId &&
                          pendingDocumentTypeId !== detailQ.data?.documentTypeId && (
                            <span
                              className="ml-2 rounded bg-accent-50 px-2 py-0.5 align-middle text-xs font-normal text-accent-700"
                              data-testid="letter-detail-pending-type-number-hint"
                            >
                              {t('tenant.documents.typePicker.numberPendingOnSave')}
                            </span>
                          )}
                      </h1>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-text-tertiary">
                        <span className="rounded bg-accent-50 px-2 py-0.5 text-accent-700">
                          {letterStatusLabel(form.status, t)}
                        </span>
                        {form.urgency && form.urgency !== 'normal' && (
                          <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-700">
                            {form.urgency}
                          </span>
                        )}
                        {isDeleted && (
                          <span className="rounded bg-red-50 px-2 py-0.5 text-red-600">
                            {t('tenant.documents.letters.detail.deletedBadge')}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <DocumentLayoutPicker
                moduleSlug="letters"
                value={layoutOverrideId}
                onChange={setLayoutOverrideId}
                myRoleId={myRoleId}
                isSuperUser={isSuperUser}
              />
              {editing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    leftIcon={<XIcon size={14} aria-hidden="true" />}
                    disabled={saveMutation.isPending}
                    data-testid="letter-detail-cancel-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="primary"
                    leftIcon={
                      isCreate ? (
                        <Plus size={14} aria-hidden="true" />
                      ) : (
                        <Save size={14} aria-hidden="true" />
                      )
                    }
                    loading={saveMutation.isPending}
                    disabled={saveMutation.isPending}
                    data-testid="letter-detail-save-btn"
                  >
                    {isCreate ? t('common.create') : t('common.save')}
                  </Button>
                </>
              ) : isDeleted ? (
                canDelete && (
                  <Button
                    onClick={() => setConfirmRestore(true)}
                    variant="primary"
                    leftIcon={<RotateCcw size={14} aria-hidden="true" />}
                    loading={restoreMutation.isPending}
                    disabled={restoreMutation.isPending}
                    data-testid="letter-detail-restore-btn"
                  >
                    {t('tenant.documents.letters.actions.restore')}
                  </Button>
                )
              ) : (
                <>
                  {canDelete && (
                    <Button
                      onClick={() => setConfirmDelete(true)}
                      variant="danger"
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Trash2 size={14} aria-hidden="true" />}
                      data-testid="letter-detail-delete-btn"
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      onClick={() => setEditing(true)}
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Pencil size={14} aria-hidden="true" />}
                      data-testid="letter-detail-edit-btn"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Zadanie przycisku SQL na piśmie (w kolejce lub w toku): pisma nie da się zapisać ani
            usunąć, „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
          {sqlButton.lockBanner}

          {/* Process approval banner */}
          {!isCreate && id && processesActive ? (
            <ProcessApprovalBanner
              moduleCode={DOCUMENTS_MODULE_CODE}
              recordId={id}
              documentTypeId={detailQ.data?.documentTypeId ?? null}
              beforeDecision={saveBeforeDecision.beforeDecision}
            />
          ) : null}

          {/* Silnik formatek (R3, unifikacja-formatek): JEDEN RecursiveFormRenderer
          dla create i edit/view, na layoucie po top-upie (zawsze zdefiniowany —
          topUpDocumentLayout syntetyzuje 'data' z core-polami gdy brak aktywnego
          layoutu w DB, więc gałąź !effectiveLayoutConfig nie jest już potrzebna).
          'data' schodzi rekurencyjnie w children i woła renderField(fieldId) per
          liść (core przez letterCoreRenderers.tsx, ext przez ExtFieldValueInput)
          — tabRenderers NIE zawiera już 'data'. Pole dodatkowe pokazuje się tylko
          tam, gdzie ktoś postawił je w układzie (top-up niczego nie dokleja).
          'parties' zostaje gated na !isCreate && id
          (self-persisting sub-resource, orthogonal do atomic save — jak przed
          migracją). Zakładki „Pola dodatkowe" już nie ma; 'audit_log' to przenośny
          blok (letter_audit_view) z komunikatem „zapisz najpierw" do czasu zapisu
          rekordu. Stary blok ext_fields_view w zamrożonym układzie renderuje się
          jako nic (RecursiveFormRenderer — typ poza PLACEHOLDER_TYPES i rejestrem).
          Tabele dodatkowe (additional_view)
          dostępne od razu w trybie buforowanym (bez HTTP, delty jadą w
          POST/PATCH — patrz saveAggregate). */}
          <RecursiveFormRenderer
            layoutConfig={engineLayout}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            tabRenderers={{
              parties: () =>
                !isCreate && id ? (
                  <DocumentPartiesEditor
                    documentId={id}
                    canManage={canManageParties}
                    companyId={detailQ.data?.companyId ?? null}
                  />
                ) : null,
            }}
            contextRenderers={{
              // dokument-blokify (2026-08-24): przenośny blok „Audit log" — mirror kartotek. Guard
              // `!isCreate && recordId` (w trybie create `id==='new'`). Samodzielnej zakładki
              // „Pola dodatkowe" już nie ma: pole dodatkowe pokazuje się tylko tam, gdzie ktoś
              // postawił je w układzie, a jego wartość jest czytana z odpowiedzi GET rekordu.
              letter_audit_view: () =>
                !isCreate && recordId ? (
                  <SystemModuleAuditTab
                    moduleSlug="letters"
                    recordId={recordId}
                    testIdPrefix="letter-audit"
                  />
                ) : (
                  <SaveFirstNotice
                    message={t('tenant.documents.letters.detail.tabDisabled.save')}
                  />
                ),
              ...buildDocumentAdditionalRenderers(recordId, {
                editable: editing,
                buffered: true,
                processesActive,
                beforeDecision: saveBeforeDecision.beforeDecision,
                documentTypeId: detailQ.data?.documentTypeId ?? null,
                onDeltasChange: handleAdditionalDeltasChange,
                getAddRowDraft,
                onAddRowDraftChange: handleAddRowDraftChange,
                // Data-loss bugfix (mirror CommercialDocDetail/InternalDocDetail):
                // pass the hook's `getBufferedValue` (recreated on every Map
                // change) into this un-memoized factory call (it runs fresh every
                // render), so a Tabs-driven remount of AdditionalTableTab reads
                // the intact CURRENT buffer instead of an empty one. See
                // AdditionalTableTab header + useAdditionalTableDeltas.ts.
                getBufferedValue,
                companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
                // F22 (widoki dodatkowe) — recordValues dla wiązania `record_field`: ext (żywe,
                // działa w create) + core przez resolver silnika formatek.
                extValues,
                coreRecordValues: buildCoreFieldRecordValues(letterEngineConfig, form),
                // Klucz wiązań `parent_field` w tabelach dodatkowych tej klasy dokumentu.
                parentKey: 'letter',
              }),
              // Klocek „Przycisk SQL": zadanie w tle na tym piśmie. Prawo = ten sam sygnał co „Edytuj”
              // (usunięte pismo nie ma „Edytuj”, więc i przycisk jest wyszarzony); serwer i tak sprawdza
              // prawo edycji z typem i etapem procesu.
              sql_button: sqlButton.renderSqlButton({
                canEdit: canEdit && !isDeleted,
                savePending: saveMutation.isPending,
              }),
              attachments_view: () =>
                recordId ? (
                  <AttachmentsSection
                    ownerType="document"
                    ownerId={recordId}
                    companyId={createCompanyId ?? detailQ.data?.companyId ?? undefined}
                  />
                ) : null,
              ...buildDocumentPreviewContextRenderer({
                ownerType: 'document',
                ownerId: recordId,
                companyId: createCompanyId ?? detailQ.data?.companyId ?? undefined,
                extValues,
              }),
              document_items_view: ({ element }) =>
                editing ? (
                  <DocumentItemsEditor
                    value={itemsDraft}
                    onChange={handleItemsChange}
                    currencyCode={detailQ.data?.currencyCode}
                    companyId={createCompanyId ?? detailQ.data?.companyId ?? null}
                    invalidRowKeys={invalidItemRowKeys}
                    columnSettings={element.settings}
                  />
                ) : (
                  <DocumentItemsSection
                    items={detailQ.data?.items}
                    vatBreakdown={detailQ.data?.vatBreakdown}
                    amountNet={detailQ.data?.amountNet}
                    amountVat={detailQ.data?.amountVat}
                    amountGross={detailQ.data?.amountGross}
                    currencyCode={detailQ.data?.currencyCode}
                    columnSettings={element.settings}
                  />
                ),
            }}
          />

          {/* Delete confirm */}
          <ConfirmDialog
            open={confirmDelete}
            title={t('tenant.documents.letters.detail.deleteConfirmTitle')}
            message={t('tenant.documents.letters.detail.deleteConfirmBody', {
              title: detailQ.data?.systemNumber ?? detailQ.data?.title ?? id,
            })}
            confirmLabel={t('common.delete')}
            variant="danger"
            onConfirm={() => {
              setConfirmDelete(false);
              deleteMutation.mutate({});
            }}
            onClose={() => setConfirmDelete(false)}
          />

          {/* Restore confirm */}
          <ConfirmDialog
            open={confirmRestore}
            title={t('tenant.documents.letters.detail.restoreConfirmTitle')}
            message={t('tenant.documents.letters.detail.restoreConfirmBody', {
              title: detailQ.data?.systemNumber ?? detailQ.data?.title ?? id,
            })}
            confirmLabel={t('tenant.documents.letters.actions.restore')}
            variant="primary"
            onConfirm={() => {
              setConfirmRestore(false);
              restoreMutation.mutate();
            }}
            onClose={() => setConfirmRestore(false)}
          />

          {controlWarning.dialog}
          {deleteControlWarning.dialog}
        </div>
      </DocumentPreviewDockHost>
    </ModuleGuard>
  );
}
